import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

// ── Types ─────────────────────────────────────────────────────────────────────
type IvrMenu = {
  id: number; menu_name: string; display_name: string; greeting_file: string;
  timeout_seconds: number; timeout_destination_type: string; timeout_destination: string;
  invalid_destination_type: string; invalid_destination: string; is_active: number;
};
type IvrOption = {
  id: number; ivr_menu_id: number; digit: string;
  destination_type: string; destination: string; description: string;
};
type TimeCondition = {
  id: number; condition_name: string; display_name: string;
  open_time: string; close_time: string; open_days: string;
  open_destination_type: string; open_destination: string;
  closed_destination_type: string; closed_destination: string; is_active: number;
};
type MohClass = {
  id: number; class_name: string; display_name: string;
  mode: string; directory: string; is_active: number;
};
type BlacklistEntry = {
  id: number; number: string; description: string; is_active: number; created_at: string;
};
type RingGroup = {
  id: number; group_number: string; group_name: string; strategy: string;
  ring_timeout: number; is_active?: number;
  members: { id: number; extension_id: number; extension_number: string; display_name: string; priority: number }[];
};
type AsteriskStatus = {
  connected: boolean; version?: string; uptime?: string; channels?: number; peers?: number;
};

// ── Shared helpers ────────────────────────────────────────────────────────────
const DEST_TYPES = [
  { value: 'extension',      label: 'Extension' },
  { value: 'queue',          label: 'Queue' },
  { value: 'ivr',            label: 'IVR Menu' },
  { value: 'time_condition', label: 'Time Condition' },
  { value: 'voicemail',      label: 'Voicemail' },
  { value: 'external',       label: 'External Number' },
  { value: 'announcement',   label: 'Announcement' },
  { value: 'hangup',         label: 'Hang up' },
];

const DAYS_OPTIONS = [
  { value: 'mon-fri',  label: 'Mon – Fri' },
  { value: 'mon-sat',  label: 'Mon – Sat' },
  { value: 'mon-sun',  label: 'Mon – Sun (all week)' },
  { value: 'sat-sun',  label: 'Sat – Sun (weekend)' },
  { value: '*',        label: 'Every day' },
];

const DIGITS = ['0','1','2','3','4','5','6','7','8','9','*','#'];
const MOH_MODES = ['files', 'quietmp3', 'mp3', 'playlist'];

function StatusBadge({ active }: { active: number | boolean }) {
  const on = !!active;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: on ? '#059669' : '#dc2626' }}>
      {on ? 'Active' : 'Inactive'}
    </span>
  );
}

function Flash({ msg }: { msg: { text: string; ok: boolean } | null }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: '10px 16px', marginBottom: 12, borderRadius: 6, fontWeight: 500,
      background: msg.ok ? '#d1fae5' : '#fee2e2',
      color: msg.ok ? '#065f46' : '#991b1b'
    }}>
      {msg.text}
    </div>
  );
}

// ── Tab: Status ───────────────────────────────────────────────────────────────
function StatusTab() {
  const [status, setStatus] = useState<AsteriskStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  function flash(text: string, ok = true) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  }

  async function load() {
    setLoading(true);
    try {
      const data = await api.asteriskStatus();
      setStatus(data);
    } catch { setStatus({ connected: false }); }
    setLoading(false);
  }

  async function reload() {
    setReloading(true);
    try {
      await api.asteriskSync();
      flash('System sync and configuration reload complete');
      await load();
    } catch (e: any) {
      flash(e?.response?.data?.error || 'Reload failed', false);
    }
    setReloading(false);
  }

  useEffect(() => { load(); }, []);

  return (
    <div>
      <Flash msg={msg} />
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button className="admin-btn" onClick={load}>↻ Refresh Status</button>
        <button className="admin-btn primary" onClick={reload} disabled={reloading}>
          {reloading ? 'Syncing...' : '⟳ Full Sync & Reload'}
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#94a3b8', padding: 20 }}>Checking system…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: 'Connection', value: status?.connected ? '✓ Online' : '✗ Offline',
              color: status?.connected ? '#059669' : '#dc2626' },
            { label: 'Version', value: status?.version || '—' },
            { label: 'Uptime', value: status?.uptime || '—' },
            { label: 'Active Channels', value: status?.channels ?? '—' },
            { label: 'SIP Peers', value: status?.peers ?? '—' },
          ].map(card => (
            <div key={card.label} style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '18px 20px'
            }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 6 }}>
                {card.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: card.color || '#1e293b' }}>
                {String(card.value)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 28, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Server Details</div>
        <table style={{ fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {[
              ['Engine Host', window.location.hostname],
              ['WSS Endpoint', 'wss://' + window.location.host + '/ws-sip'],
              ['STUN/TURN Server', window.location.hostname + ':3478'],
              ['Public IP', '151.253.188.4'],
              ['Config Path', '/etc/telephony/'],
            ].map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '5px 12px 5px 0', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{k}</td>
                <td style={{ padding: '5px 0', fontFamily: 'monospace', fontSize: 12 }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: IVR Menus ────────────────────────────────────────────────────────────
const EMPTY_IVR = {
  menu_name: '', display_name: '', greeting_file: 'silence/1', timeout_seconds: '10',
  timeout_destination_type: 'hangup', timeout_destination: '',
  invalid_destination_type: 'hangup', invalid_destination: '', is_active: true
};

function IvrTab() {
  const [menus, setMenus]       = useState<IvrMenu[]>([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState<any>(EMPTY_IVR);
  const [editing, setEditing]   = useState<IvrMenu | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);
  const [selected, setSelected] = useState<IvrMenu | null>(null);
  const [opts, setOpts]         = useState<IvrOption[]>([]);
  const [optForm, setOptForm]   = useState({ digit: '1', destination_type: 'extension', destination: '', description: '' });

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); }

  async function load() {
    setLoading(true);
    try { setMenus(await api.getIvrMenus()); } catch { setMenus([]); }
    setLoading(false);
  }

  async function loadOpts(menu: IvrMenu) {
    setSelected(menu);
    try { setOpts(await api.getIvrOptions(menu.id)); } catch { setOpts([]); }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const payload = { ...form, timeout_seconds: parseInt(form.timeout_seconds) || 10, is_active: form.is_active ? 1 : 0 };
      if (editing) await api.updateIvrMenu(editing.id, payload);
      else await api.createIvrMenu(payload);
      flash(editing ? 'IVR menu updated' : 'IVR menu created and provisioned');
      setShowForm(false);
      load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Save failed', false); }
  }

  async function remove(m: IvrMenu) {
    if (!window.confirm(`Delete IVR menu "${m.menu_name}"?`)) return;
    try { await api.deleteIvrMenu(m.id); flash('Deleted'); if (selected?.id === m.id) setSelected(null); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Delete failed', false); }
  }

  async function saveOpt() {
    if (!selected) return;
    try {
      await api.createIvrOption(selected.id, optForm);
      flash('Option saved');
      loadOpts(selected);
      setOptForm({ digit: '1', destination_type: 'extension', destination: '', description: '' });
    } catch (e: any) { flash(e?.response?.data?.error || 'Failed', false); }
  }

  async function removeOpt(opt: IvrOption) {
    if (!selected) return;
    try { await api.deleteIvrOption(selected.id, opt.id); loadOpts(selected); }
    catch (e: any) { flash(e?.response?.data?.error || 'Failed', false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 6, color: '#64748b', fontSize: 13 }}>
        Create IVR menus with DTMF key routing. Changes are provisioned automatically.
      </div>
      <Flash msg={msg} />
      <button className="admin-btn primary" style={{ marginBottom: 16 }}
        onClick={() => { setEditing(null); setForm(EMPTY_IVR); setShowForm(true); }}>
        + Add IVR Menu
      </button>

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>{editing ? `Edit: ${editing.menu_name}` : 'New IVR Menu'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <label>
              <div className="field-label">Menu Name *</div>
              <input className="admin-input" placeholder="main-menu"
                value={form.menu_name} disabled={!!editing}
                onChange={e => setForm({ ...form, menu_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Display Name</div>
              <input className="admin-input" placeholder="Main Menu"
                value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Greeting File (sound file)</div>
              <input className="admin-input" placeholder="welcome" value={form.greeting_file}
                onChange={e => setForm({ ...form, greeting_file: e.target.value })} />
            </label>
            <label>
              <div className="field-label">DTMF Timeout (seconds)</div>
              <input className="admin-input" type="number" value={form.timeout_seconds}
                onChange={e => setForm({ ...form, timeout_seconds: e.target.value })} />
            </label>
            <label>
              <div className="field-label">On Timeout — Route To</div>
              <select className="admin-input" value={form.timeout_destination_type}
                onChange={e => setForm({ ...form, timeout_destination_type: e.target.value })}>
                {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">Timeout Destination</div>
              <input className="admin-input" placeholder="e.g. 101 or queue-name"
                value={form.timeout_destination}
                onChange={e => setForm({ ...form, timeout_destination: e.target.value })} />
            </label>
            <label>
              <div className="field-label">On Invalid Key — Route To</div>
              <select className="admin-input" value={form.invalid_destination_type}
                onChange={e => setForm({ ...form, invalid_destination_type: e.target.value })}>
                {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">Invalid Destination</div>
              <input className="admin-input" placeholder="leave blank to re-prompt"
                value={form.invalid_destination}
                onChange={e => setForm({ ...form, invalid_destination: e.target.value })} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            <span style={{ fontSize: 13 }}>Active</span>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn primary" onClick={save}>{editing ? 'Update' : 'Create'}</button>
            <button className="admin-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            {menus.length === 0
              ? <div style={{ color: '#94a3b8', padding: 20 }}>No IVR menus yet.</div>
              : (
                <table className="admin-table">
                  <thead>
                    <tr><th>Name</th><th>Greeting</th><th>Timeout</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {menus.map(m => (
                      <tr key={m.id} style={{ background: selected?.id === m.id ? '#f0f9ff' : undefined }}>
                        <td><strong>{m.menu_name}</strong><br /><span style={{ fontSize: 12, color: '#64748b' }}>{m.display_name}</span></td>
                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{m.greeting_file}</td>
                        <td>{m.timeout_seconds}s</td>
                        <td><StatusBadge active={m.is_active} /></td>
                        <td>
                          <button className="admin-btn sm" onClick={() => loadOpts(m)}>Keys</button>{' '}
                          <button className="admin-btn sm" onClick={() => { setEditing(m); setForm({ ...m, is_active: !!m.is_active }); setShowForm(true); }}>Edit</button>{' '}
                          <button className="admin-btn sm danger" onClick={() => remove(m)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          {selected && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>Keys: {selected.menu_name}</div>
                <button className="admin-btn sm" onClick={() => setSelected(null)}>✕ Close</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr auto', gap: 8, marginBottom: 10, alignItems: 'end' }}>
                <label>
                  <div className="field-label">Digit</div>
                  <select className="admin-input" value={optForm.digit}
                    onChange={e => setOptForm({ ...optForm, digit: e.target.value })}>
                    {DIGITS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label>
                  <div className="field-label">Route To</div>
                  <select className="admin-input" value={optForm.destination_type}
                    onChange={e => setOptForm({ ...optForm, destination_type: e.target.value })}>
                    {DEST_TYPES.filter(d => d.value !== 'hangup').map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </label>
                <label>
                  <div className="field-label">Destination</div>
                  <input className="admin-input" placeholder="101 / queue-name"
                    value={optForm.destination}
                    onChange={e => setOptForm({ ...optForm, destination: e.target.value })} />
                </label>
                <label>
                  <div className="field-label">Label</div>
                  <input className="admin-input" placeholder="Sales" value={optForm.description}
                    onChange={e => setOptForm({ ...optForm, description: e.target.value })} />
                </label>
                <button className="admin-btn primary" style={{ marginBottom: 1 }} onClick={saveOpt}>Add</button>
              </div>
              {opts.length === 0
                ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No keys configured yet.</div>
                : (
                  <table className="admin-table">
                    <thead><tr><th>Key</th><th>Type</th><th>Destination</th><th>Label</th><th></th></tr></thead>
                    <tbody>
                      {opts.map(o => (
                        <tr key={o.id}>
                          <td style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 16 }}>{o.digit}</td>
                          <td style={{ fontSize: 12 }}>{o.destination_type}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.destination}</td>
                          <td style={{ fontSize: 12, color: '#64748b' }}>{o.description}</td>
                          <td><button className="admin-btn sm danger" onClick={() => removeOpt(o)}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: Time Conditions ──────────────────────────────────────────────────────
const EMPTY_TC = {
  condition_name: '', display_name: '', open_time: '09:00', close_time: '18:00',
  open_days: 'mon-fri', open_destination_type: 'extension', open_destination: '',
  closed_destination_type: 'voicemail', closed_destination: '', is_active: true
};

function TimeConditionsTab() {
  const [items, setItems]       = useState<TimeCondition[]>([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState<any>(EMPTY_TC);
  const [editing, setEditing]   = useState<TimeCondition | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); }
  async function load() {
    setLoading(true);
    try { setItems(await api.getTimeConditions()); } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editing) await api.updateTimeCondition(editing.id, payload);
      else await api.createTimeCondition(payload);
      flash(editing ? 'Updated and provisioned' : 'Created and provisioned');
      setShowForm(false); load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Save failed', false); }
  }

  async function remove(tc: TimeCondition) {
    if (!window.confirm(`Delete time condition "${tc.condition_name}"?`)) return;
    try { await api.deleteTimeCondition(tc.id); flash('Deleted'); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Delete failed', false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 6, color: '#64748b', fontSize: 13 }}>
        Route calls differently based on time of day and day of week. Reference as context <code>tc-conditionname</code> in DID routing.
      </div>
      <Flash msg={msg} />
      <button className="admin-btn primary" style={{ marginBottom: 16 }}
        onClick={() => { setEditing(null); setForm(EMPTY_TC); setShowForm(true); }}>
        + Add Time Condition
      </button>

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>
            {editing ? `Edit: ${editing.condition_name}` : 'New Time Condition'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <label>
              <div className="field-label">Condition Name *</div>
              <input className="admin-input" placeholder="business-hours" disabled={!!editing}
                value={form.condition_name} onChange={e => setForm({ ...form, condition_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Display Name</div>
              <input className="admin-input" placeholder="Business Hours"
                value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Open From</div>
              <input className="admin-input" type="time" value={form.open_time}
                onChange={e => setForm({ ...form, open_time: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Open Until</div>
              <input className="admin-input" type="time" value={form.close_time}
                onChange={e => setForm({ ...form, close_time: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Open Days</div>
              <select className="admin-input" value={form.open_days}
                onChange={e => setForm({ ...form, open_days: e.target.value })}>
                {DAYS_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <div />
            <label>
              <div className="field-label">During Hours — Route To</div>
              <select className="admin-input" value={form.open_destination_type}
                onChange={e => setForm({ ...form, open_destination_type: e.target.value })}>
                {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">During Hours Destination</div>
              <input className="admin-input" placeholder="e.g. 101 or queue-name"
                value={form.open_destination} onChange={e => setForm({ ...form, open_destination: e.target.value })} />
            </label>
            <label>
              <div className="field-label">After Hours — Route To</div>
              <select className="admin-input" value={form.closed_destination_type}
                onChange={e => setForm({ ...form, closed_destination_type: e.target.value })}>
                {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">After Hours Destination</div>
              <input className="admin-input" placeholder="e.g. 100 for voicemail"
                value={form.closed_destination} onChange={e => setForm({ ...form, closed_destination: e.target.value })} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            <span style={{ fontSize: 13 }}>Active</span>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn primary" onClick={save}>{editing ? 'Update' : 'Create'}</button>
            <button className="admin-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div>
        : items.length === 0
          ? <div style={{ color: '#94a3b8', padding: 20 }}>No time conditions yet.</div>
          : (
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Hours</th><th>Days</th><th>Open Route</th><th>Closed Route</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {items.map(tc => (
                  <tr key={tc.id}>
                    <td><strong>{tc.condition_name}</strong><br /><span style={{ fontSize: 12, color: '#64748b' }}>{tc.display_name}</span></td>
                    <td style={{ fontSize: 12 }}>{tc.open_time} – {tc.close_time}</td>
                    <td style={{ fontSize: 12 }}>{tc.open_days}</td>
                    <td style={{ fontSize: 12 }}><span style={{ color: '#059669' }}>{tc.open_destination_type}</span>: {tc.open_destination}</td>
                    <td style={{ fontSize: 12 }}><span style={{ color: '#dc2626' }}>{tc.closed_destination_type}</span>: {tc.closed_destination}</td>
                    <td><StatusBadge active={tc.is_active} /></td>
                    <td>
                      <button className="admin-btn sm" onClick={() => { setEditing(tc); setForm({ ...tc, is_active: !!tc.is_active }); setShowForm(true); }}>Edit</button>{' '}
                      <button className="admin-btn sm danger" onClick={() => remove(tc)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}

// ── Tab: Music on Hold ────────────────────────────────────────────────────────
const EMPTY_MOH = { class_name: '', display_name: '', mode: 'files', directory: '/var/lib/moh', is_active: true };

function MohTab() {
  const [items, setItems]       = useState<MohClass[]>([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState<any>(EMPTY_MOH);
  const [editing, setEditing]   = useState<MohClass | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); }
  async function load() {
    setLoading(true);
    try { setItems(await api.getMohClasses()); } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editing) await api.updateMohClass(editing.id, payload);
      else await api.createMohClass(payload);
      flash(editing ? 'Updated and provisioned' : 'Created and provisioned');
      setShowForm(false); load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Save failed', false); }
  }

  async function remove(cls: MohClass) {
    if (!window.confirm(`Delete MoH class "${cls.class_name}"?`)) return;
    try { await api.deleteMohClass(cls.id); flash('Deleted'); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Delete failed', false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 6, color: '#64748b', fontSize: 13 }}>
        Manage Music on Hold classes. Files should be placed in the directory on the media server.
      </div>
      <Flash msg={msg} />
      <button className="admin-btn primary" style={{ marginBottom: 16 }}
        onClick={() => { setEditing(null); setForm(EMPTY_MOH); setShowForm(true); }}>
        + Add MoH Class
      </button>

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>{editing ? `Edit: ${editing.class_name}` : 'New MoH Class'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <label>
              <div className="field-label">Class Name *</div>
              <input className="admin-input" placeholder="default" disabled={!!editing}
                value={form.class_name} onChange={e => setForm({ ...form, class_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Display Name</div>
              <input className="admin-input" placeholder="Default Hold Music"
                value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Mode</div>
              <select className="admin-input" value={form.mode}
                onChange={e => setForm({ ...form, mode: e.target.value })}>
                {MOH_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">Directory (on Asterisk server)</div>
              <input className="admin-input" placeholder="/var/lib/moh"
                value={form.directory} onChange={e => setForm({ ...form, directory: e.target.value })} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            <span style={{ fontSize: 13 }}>Active</span>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn primary" onClick={save}>{editing ? 'Update' : 'Create'}</button>
            <button className="admin-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div>
        : items.length === 0
          ? <div style={{ color: '#94a3b8', padding: 20 }}>No MoH classes yet. The default Music on Hold is always available.</div>
          : (
            <table className="admin-table">
              <thead><tr><th>Class Name</th><th>Display Name</th><th>Mode</th><th>Directory</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {items.map(cls => (
                  <tr key={cls.id}>
                    <td><strong style={{ fontFamily: 'monospace' }}>{cls.class_name}</strong></td>
                    <td>{cls.display_name}</td>
                    <td style={{ fontSize: 12 }}>{cls.mode}</td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b' }}>{cls.directory}</td>
                    <td><StatusBadge active={cls.is_active} /></td>
                    <td>
                      <button className="admin-btn sm" onClick={() => { setEditing(cls); setForm({ ...cls, is_active: !!cls.is_active }); setShowForm(true); }}>Edit</button>{' '}
                      <button className="admin-btn sm danger" onClick={() => remove(cls)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}

// ── Tab: Blacklist ────────────────────────────────────────────────────────────
function BlacklistTab() {
  const [items, setItems]     = useState<BlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ number: '', description: '', is_active: true });
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]   = useState<number | null>(null);
  const [msg, setMsg]         = useState<{ text: string; ok: boolean } | null>(null);

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); }
  async function load() {
    setLoading(true);
    try { setItems(await api.getBlacklist()); } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editId) await api.updateBlacklist(editId, payload);
      else await api.createBlacklist(payload);
      flash(editId ? 'Updated and provisioned' : 'Number blocked');
      setShowForm(false); setEditId(null); setForm({ number: '', description: '', is_active: true }); load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Save failed', false); }
  }

  async function remove(entry: BlacklistEntry) {
    if (!window.confirm(`Remove ${entry.number} from blacklist?`)) return;
    try { await api.deleteBlacklist(entry.id); flash('Number unblocked'); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Delete failed', false); }
  }

  async function toggle(entry: BlacklistEntry) {
    try {
      await api.updateBlacklist(entry.id, { ...entry, is_active: entry.is_active ? 0 : 1 });
      load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Failed', false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 6, color: '#64748b', fontSize: 13 }}>
        Blocked numbers are stored in the system database and checked at call time.
      </div>
      <Flash msg={msg} />
      <button className="admin-btn primary" style={{ marginBottom: 16 }}
        onClick={() => { setEditId(null); setForm({ number: '', description: '', is_active: true }); setShowForm(true); }}>
        + Block Number
      </button>

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>{editId ? 'Edit Entry' : 'Block a Number'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <label>
              <div className="field-label">Phone Number *</div>
              <input className="admin-input" placeholder="+97150xxxxxxx or 04xxxxxxx"
                value={form.number} disabled={!!editId}
                onChange={e => setForm({ ...form, number: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Description / Reason</div>
              <input className="admin-input" placeholder="Spam caller, nuisance…"
                value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            <span style={{ fontSize: 13 }}>Blocking active</span>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn primary" onClick={save}>{editId ? 'Update' : 'Block'}</button>
            <button className="admin-btn" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div>
        : items.length === 0
          ? <div style={{ color: '#94a3b8', padding: 20 }}>No blocked numbers.</div>
          : (
            <table className="admin-table">
              <thead><tr><th>Number</th><th>Description</th><th>Blocking</th><th>Added</th><th>Actions</th></tr></thead>
              <tbody>
                {items.map(entry => (
                  <tr key={entry.id}>
                    <td><strong style={{ fontFamily: 'monospace' }}>{entry.number}</strong></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{entry.description || '—'}</td>
                    <td>
                      <button
                        onClick={() => toggle(entry)}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: entry.is_active ? '#d1fae5' : '#fee2e2',
                          color: entry.is_active ? '#065f46' : '#991b1b'
                        }}>
                        {entry.is_active ? 'Blocked' : 'Disabled'}
                      </button>
                    </td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(entry.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="admin-btn sm" onClick={() => {
                        setEditId(entry.id);
                        setForm({ number: entry.number, description: entry.description, is_active: !!entry.is_active });
                        setShowForm(true);
                      }}>Edit</button>{' '}
                      <button className="admin-btn sm danger" onClick={() => remove(entry)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}

// ── Tab: Ring Groups ──────────────────────────────────────────────────────────
function RingGroupsTab() {
  const [groups, setGroups]     = useState<RingGroup[]>([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState<RingGroup | null>(null);
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);
  const [form, setForm]         = useState({ group_number: '', group_name: '', strategy: 'ringall', ring_timeout: '30' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<RingGroup | null>(null);
  const [extNum, setExtNum]     = useState('');

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); }
  const load = useCallback(async () => {
    setLoading(true);
    try { setGroups(await api.getAsteriskRingGroups()); } catch { setGroups([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    try {
      const payload = { ...form, ring_timeout: parseInt(form.ring_timeout) || 30, tenant_id: null };
      if (editing) await api.updateRingGroup(editing.id, payload);
      else await api.createRingGroup(payload);
      flash(editing ? 'Ring group updated' : 'Ring group created');
      setShowForm(false); load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Save failed', false); }
  }

  async function remove(g: RingGroup) {
    if (!window.confirm(`Delete ring group "${g.group_name}"?`)) return;
    try { await api.deleteRingGroup(g.id); flash('Deleted'); if (selected?.id === g.id) setSelected(null); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Delete failed', false); }
  }

  async function addMember(g: RingGroup) {
    if (!extNum.trim()) return;
    try {
      const exts = await api.getExtensions();
      const ext = exts.find((e: any) => e.extension_number === extNum.trim());
      if (!ext) { flash(`Extension ${extNum} not found`, false); return; }
      await api.addRingGroupMember(g.id, { extension_id: ext.id, priority: 1 });
      flash('Member added');
      setExtNum('');
      load().then(() => {
        setGroups(prev => {
          const updated = prev.find(x => x.id === g.id);
          if (updated) setSelected(updated);
          return prev;
        });
      });
    } catch (e: any) { flash(e?.response?.data?.error || 'Failed', false); }
  }

  async function removeMember(g: RingGroup, extId: number) {
    try {
      await api.removeRingGroupMember(g.id, extId);
      flash('Member removed');
      load();
    } catch (e: any) { flash(e?.response?.data?.error || 'Failed', false); }
  }

  const STRATEGIES = ['ringall', 'leastrecent', 'fewestcalls', 'random', 'rrmemory', 'linear'];

  return (
    <div>
      <div style={{ marginBottom: 6, color: '#64748b', fontSize: 13 }}>
        Ring groups simultaneously ring multiple extensions. Changes are provisioned automatically.
      </div>
      <Flash msg={msg} />
      <button className="admin-btn primary" style={{ marginBottom: 16 }}
        onClick={() => { setEditing(null); setForm({ group_number: '', group_name: '', strategy: 'ringall', ring_timeout: '30' }); setShowForm(true); }}>
        + Add Ring Group
      </button>

      {showForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>{editing ? `Edit: ${editing.group_name}` : 'New Ring Group'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <label>
              <div className="field-label">Group Number *</div>
              <input className="admin-input" placeholder="600" value={form.group_number}
                onChange={e => setForm({ ...form, group_number: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Group Name *</div>
              <input className="admin-input" placeholder="Sales Team" value={form.group_name}
                onChange={e => setForm({ ...form, group_name: e.target.value })} />
            </label>
            <label>
              <div className="field-label">Ring Strategy</div>
              <select className="admin-input" value={form.strategy}
                onChange={e => setForm({ ...form, strategy: e.target.value })}>
                {STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>
              <div className="field-label">Ring Timeout (seconds)</div>
              <input className="admin-input" type="number" value={form.ring_timeout}
                onChange={e => setForm({ ...form, ring_timeout: e.target.value })} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn primary" onClick={save}>{editing ? 'Update' : 'Create'}</button>
            <button className="admin-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            {groups.length === 0
              ? <div style={{ color: '#94a3b8', padding: 20 }}>No ring groups yet.</div>
              : (
                <table className="admin-table">
                  <thead><tr><th>Number</th><th>Name</th><th>Strategy</th><th>Timeout</th><th>Members</th><th>Actions</th></tr></thead>
                  <tbody>
                    {groups.map(g => (
                      <tr key={g.id} style={{ background: selected?.id === g.id ? '#f0f9ff' : undefined }}>
                        <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{g.group_number}</td>
                        <td>{g.group_name}</td>
                        <td style={{ fontSize: 12 }}>{g.strategy}</td>
                        <td>{g.ring_timeout}s</td>
                        <td>{g.members?.length ?? 0}</td>
                        <td>
                          <button className="admin-btn sm" onClick={() => setSelected(selected?.id === g.id ? null : g)}>Members</button>{' '}
                          <button className="admin-btn sm" onClick={() => { setEditing(g); setForm({ group_number: g.group_number, group_name: g.group_name, strategy: g.strategy, ring_timeout: String(g.ring_timeout) }); setShowForm(true); }}>Edit</button>{' '}
                          <button className="admin-btn sm danger" onClick={() => remove(g)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          {selected && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>Members: {selected.group_name}</div>
                <button className="admin-btn sm" onClick={() => setSelected(null)}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input className="admin-input" placeholder="Extension number e.g. 101" style={{ flex: 1 }}
                  value={extNum} onChange={e => setExtNum(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addMember(selected)} />
                <button className="admin-btn primary" onClick={() => addMember(selected)}>Add</button>
              </div>
              {(groups.find(g => g.id === selected.id)?.members || []).length === 0
                ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No members yet.</div>
                : (
                  <table className="admin-table">
                    <thead><tr><th>Ext</th><th>Name</th><th></th></tr></thead>
                    <tbody>
                      {(groups.find(g => g.id === selected.id)?.members || []).map(m => (
                        <tr key={m.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{m.extension_number}</td>
                          <td>{m.display_name}</td>
                          <td><button className="admin-btn sm danger" onClick={() => removeMember(selected, m.extension_id)}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main AsteriskPage ─────────────────────────────────────────────────────────
const TABS = ['Status', 'Ring Groups', 'IVR Menus', 'Time Conditions', 'Music on Hold', 'Blacklist'];

export default function AsteriskPage() {
  const [tab, setTab] = useState('Status');

  return (
    <div>
      <div className="admin-page-title">Telephony Engine</div>
      <div className="admin-page-subtitle">
        All changes are provisioned to the telephony system automatically.
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e2e8f0' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: 'transparent',
              color: tab === t ? '#7c3aed' : '#64748b',
              borderBottom: tab === t ? '2px solid #7c3aed' : '2px solid transparent',
              marginBottom: -2, transition: 'color 0.15s'
            }}>
            {t}
          </button>
        ))}
      </div>

      <div>
        {tab === 'Status'         && <StatusTab />}
        {tab === 'Ring Groups'    && <RingGroupsTab />}
        {tab === 'IVR Menus'      && <IvrTab />}
        {tab === 'Time Conditions' && <TimeConditionsTab />}
        {tab === 'Music on Hold'  && <MohTab />}
        {tab === 'Blacklist'      && <BlacklistTab />}
      </div>
    </div>
  );
}
